// src/pages/transport-requests/TransportRequestOverview.tsx
import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    Container,
    Paper,
    Stack,
    Title,
    Text,
    Button,
    Group,
    Center,
    ThemeIcon,
    Divider,
    Alert,
} from "@mantine/core";
import {
    IconPlaneArrival,
    IconPlaneDeparture,
    IconArrowsLeftRight,
    IconAlertCircle,
    IconSearch,
    IconEdit,
} from "@tabler/icons-react";

const RED = "#ff5e5e";

export default function TransportRequestOverview() {
    const nav = useNavigate();
    const { eventId } = useParams<{ eventId: string }>();
    const { userId } = useParams<{ userId: string }>();

    const goArrival = () => {
        if (!eventId) return;
        nav(`/transport/arrival/${eventId}/${userId}`);
    };

    const goDeparture = () => {
        if (!eventId) return;
        nav(`/transport/departure/${eventId}/${userId}`);
    };

    const goMyBookings = () => {
        if (!eventId) return;
        nav(`/transport/my-bookings/${eventId}/${userId}`);
    };

    return (
        <Container size="sm" py="xl">
            <Paper
                withBorder
                p="lg"
                radius="md"
                style={{
                    borderColor: "rgba(255, 94, 94, 0.35)",
                    boxShadow: "0 10px 30px rgba(0,0,0,0.06)",
                }}
            >
                <Center>
                    <Stack gap="xs" style={{ width: "100%" }}>
                        <Group justify="center" gap="sm">
                            <ThemeIcon
                                radius="xl"
                                size={40}
                                variant="light"
                                style={{ background: "rgba(255,94,94,0.12)", color: RED }}
                            >
                                <IconArrowsLeftRight size={22} />
                            </ThemeIcon>

                            <Stack gap={0} style={{ textAlign: "center" }}>
                                <Title order={3}>Transport requests</Title>
                                <Text c="dimmed" size="sm">
                                    Choose Arrival (landing) or Departure (takeoff)
                                </Text>
                            </Stack>
                        </Group>

                        {!eventId && (
                            <Alert
                                mt="sm"
                                icon={<IconAlertCircle size={18} />}
                                title="Mangler eventId i URL"
                                color="red"
                                variant="light"
                            >
                                Denne side forventer en route som fx <b>/transport/requests/123</b>.
                                <br />
                                Lige nu er eventId <b>undefined</b>, derfor bliver linket forkert.
                            </Alert>
                        )}

                        <Divider my="sm" />

                        <Group justify="center" mt="xs" wrap="wrap">
                            <Button
                                leftSection={<IconPlaneArrival size={18} />}
                                onClick={goArrival}
                                disabled={!eventId}
                                style={{
                                    backgroundColor: RED,
                                    border: `1px solid ${RED}`,
                                }}
                                styles={{
                                    root: {
                                        transition:
                                            "transform 120ms ease, box-shadow 120ms ease, background-color 120ms ease",
                                        "&:hover": {
                                            transform: "translateY(-1px)",
                                            boxShadow: "0 10px 18px rgba(255,94,94,0.25)",
                                            backgroundColor: RED,
                                        },
                                        "&:disabled": {
                                            opacity: 0.5,
                                            cursor: "not-allowed",
                                        },
                                    },
                                }}
                            >
                                Create Arrival
                            </Button>

                            <Button
                                leftSection={<IconPlaneDeparture size={18} />}
                                onClick={goDeparture}
                                disabled={!eventId}
                                variant="outline"
                                style={{
                                    borderColor: RED,
                                    color: RED,
                                }}
                                styles={{
                                    root: {
                                        transition:
                                            "transform 120ms ease, box-shadow 120ms ease, background-color 120ms ease, color 120ms ease",
                                        "&:hover": {
                                            transform: "translateY(-1px)",
                                            boxShadow: "0 10px 18px rgba(255,94,94,0.22)",
                                            backgroundColor: RED,
                                            color: "white",
                                        },
                                        "&:disabled": {
                                            opacity: 0.5,
                                            cursor: "not-allowed",
                                        },
                                    },
                                }}
                            >
                                Create Departure
                            </Button>
                        </Group>

                        <Divider my="lg" />

                        <Paper
                            withBorder
                            p="md"
                            radius="md"
                            style={{
                                borderColor: "rgba(255, 94, 94, 0.25)",
                                background: "rgba(255, 94, 94, 0.03)",
                            }}
                        >
                            <Group justify="space-between" align="center" wrap="wrap">
                                <Group gap="sm">
                                    <ThemeIcon
                                        radius="xl"
                                        variant="light"
                                        style={{ background: "rgba(255,94,94,0.12)", color: RED }}
                                    >
                                        <IconEdit size={18} />
                                    </ThemeIcon>

                                    <Stack gap={0}>
                                        <Text fw={700}>Already created a booking?</Text>
                                        <Text c="dimmed" size="sm">
                                            Find your bookings and edit them
                                        </Text>
                                    </Stack>
                                </Group>

                                <Button
                                    leftSection={<IconSearch size={16} />}
                                    variant="light"
                                    color="red"
                                    onClick={goMyBookings}
                                    disabled={!eventId}
                                >
                                    Find my bookings
                                </Button>
                            </Group>
                        </Paper>
                    </Stack>
                </Center>
            </Paper>
        </Container>
    );
}